import {
  Box,
  Burger,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { signOut } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { auth } from "../firebase";
import { Logo } from "./Logo";
import { PlainTextLink } from "./PlainTextLink";
import { buildRoute, PageRoutes } from "../routes";
import { useUser } from "../hooks/useUser";
import { useUserAdmin } from "../hooks/useUserAdmin";
import { useChurches } from "../hooks/useChurches";
import { ThemeToggle } from "./ThemeToggle";

const DefaultHeader = ({ onNavigate }: { onNavigate?: () => void }) => (
  <>
    <PlainTextLink className="lw-nav-link" to={PageRoutes.HOME} onClick={onNavigate}>
      <Text className="lw-nav-link">Home</Text>
    </PlainTextLink>
    <PlainTextLink className="lw-nav-link" to={PageRoutes.PRICING} onClick={onNavigate}>
      <Text className="lw-nav-link">Pricing</Text>
    </PlainTextLink>
    <Box component="a" href="/#faq" className="lw-nav-link" onClick={onNavigate}>FAQ</Box>
  </>
);

interface HomeLink {
  label: string;
  to: string;
}

/** One church links straight to it by name; several churches link to the chooser. */
const useHomeLink = (userId?: string): HomeLink | null => {
  const { isAdmin, loading: loadingAdmin } = useUserAdmin();
  const { churches, loading: loadingChurches } = useChurches({ userId, includeAll: isAdmin });
  if (!userId || loadingAdmin || loadingChurches) return null;
  if (churches.length === 1 && !isAdmin) {
    return {
      label: churches[0].name,
      to: buildRoute(PageRoutes.CHURCH_PAGE, { churchId: churches[0].id }),
    };
  }
  return { label: churches.length > 1 ? "Churches" : "Dashboard", to: PageRoutes.DASHBOARD };
};

const AuthHeader = ({
  userEmail,
  homeLink,
  logoutUser,
  onNavigate,
}: {
  userEmail: string;
  homeLink: HomeLink | null;
  logoutUser: () => void;
  onNavigate?: () => void;
}) => (
  <>
    {homeLink && (
      <PlainTextLink className="lw-nav-link" to={homeLink.to} onClick={onNavigate}>
        <Text className="lw-nav-link" truncate maw={220}>
          {homeLink.label}
        </Text>
      </PlainTextLink>
    )}
    <Tooltip label={userEmail || ""} position="bottom" withArrow>
      <Text className="lw-nav-link"
        onClick={() => {
          logoutUser();
          onNavigate?.();
        }}
        style={{ cursor: "pointer" }}
      >
        Logout
      </Text>
    </Tooltip>
  </>
);

const Header = () => {
  const navigate = useNavigate();
  const user = useUser();
  const isAnonymousUser = user?.isAnonymous;
  const homeLink = useHomeLink(user && !isAnonymousUser ? user.uid : undefined);
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const logoutUser = async () => {
    try {
      await signOut(auth);
      notifications.show({
        title: "Success!",
        message: "You have been logged out.",
        position: "bottom-right",
      });

      navigate(PageRoutes.HOME);
    } catch (e) {
      console.error(e);

      notifications.show({
        title: "Success!",
        message: "You have been logged out.",
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  const closeMobileNav = () => {
    closeDrawer();
  };

  const unauthenticatedControls = (
    <>
      <DefaultHeader onNavigate={closeMobileNav} />
      <ThemeToggle />
      <PlainTextLink to={PageRoutes.LOGIN} onClick={closeMobileNav}>
        <Text className="lw-nav-link">Login</Text>
      </PlainTextLink>
      <Button component="a" href="/#pilot-form" onClick={closeMobileNav}>Run a free Sunday pilot</Button>
    </>
  );

  return (
    <Box
      component="header"
      w="100%"
      className="lw-header"
    >
      <Container size="xl" px={{ base: "md", md: "xl" }}>
          <Group justify="space-between" h={72}>
          <Link to={PageRoutes.HOME}>
            <Logo height={50} />
          </Link>

          <Group component="nav" gap="lg" visibleFrom="sm">
            {(!user || isAnonymousUser) && <DefaultHeader />}
            {!!user && !isAnonymousUser ? (
              <AuthHeader
                userEmail={user.email || ""}
                homeLink={homeLink}
                logoutUser={logoutUser}
              />
            ) : (
              <>
              <PlainTextLink className="lw-nav-link" to={PageRoutes.LOGIN}>
                  <Text className="lw-nav-link">Login</Text>
                </PlainTextLink>
                <Button component="a" href="/#pilot-form" size="sm">Run a free Sunday pilot</Button>
              </>
            )}
            <ThemeToggle />
          </Group>

          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            aria-label={drawerOpened ? "Close navigation" : "Open navigation"}
            hiddenFrom="sm"
          />
        </Group>
      </Container>

      <Drawer
        hiddenFrom="sm"
        opened={drawerOpened}
        onClose={closeDrawer}
        padding="md"
        size="80%"
        title={<Logo height={40} />}
      >
        <Stack gap="md">
          <Divider my="sm" />
          {(!user || isAnonymousUser) && unauthenticatedControls}
          {!!user && !isAnonymousUser && (
            <>
              <AuthHeader
                userEmail={user.email || ""}
                homeLink={homeLink}
                logoutUser={logoutUser}
                onNavigate={closeMobileNav}
              />
              <ThemeToggle />
            </>
          )}
        </Stack>
      </Drawer>
    </Box>
  );
};

export default Header;
